import { action, makeObservable, observable } from "mobx"
import { ErrorHandler } from "../../error/ErrorHandler"
import { TableConfig } from "../../components/DataTable/types/TableConfig"
import { ViewModel } from "../ViewModel"
import { FileService } from "../../../domain/services/FileService"
import { GetInvoicesUseCase } from "../../../domain/useCases/billing/getInvoices"
import { Invoice, InvoiceTransaction } from "../../../domain/entities/Invoice"
import { InvoicesFilter } from "../../../domain/types/billing/InvoicesFilter"
import { InvoiceStatus } from "../../../domain/enum/invoiceStatus.enum"
import { ExportInvoicesUseCase } from "../../../domain/useCases/billing/exportInvoices"
import { UpdateInvoicesStatusUseCase } from "../../../domain/useCases/billing/updateInvoicesStatus"
import { UpdateTransactionCreatedDateUseCase } from "../../../domain/useCases/billing/updateTransactions"
import { ExportInvoiceTransactionsUseCase } from "../../../domain/useCases/billing/exportInvoiceTransactions"
import { UpdateInvoiceUseCase } from "../../../domain/useCases/billing/updateInvoice"

interface Props {
	ErrorHandler: ErrorHandler
	GetInvoicesUseCase: GetInvoicesUseCase
	ExportInvoicesUseCase: ExportInvoicesUseCase
	ExportInvoiceTransactionsUseCase: ExportInvoiceTransactionsUseCase
	UpdateInvoicesStatusUseCase: UpdateInvoicesStatusUseCase
	UpdateInvoiceUseCase: UpdateInvoiceUseCase
	UpdateTransactionCreatedDateUseCase: UpdateTransactionCreatedDateUseCase
}
type ErrorType = "header" | "detail" | undefined
type Error = { message: string; type: ErrorType }
export type InvoiceAggregate = Omit<Omit<Omit<Invoice, "id">, "invoiceNumber">, "status"> & {
	id?: number
	invoiceNumber?: string
	status?: string
} & { invoices: Invoice[] }

export class BillingViewModel extends ViewModel {
	private _errorHandler
	private _getInvoicesUseCase: GetInvoicesUseCase
	private _exportInvoicesUseCase: ExportInvoicesUseCase
	private _exportInvoiceTransactionsUseCase: ExportInvoiceTransactionsUseCase
	private _updateInvoicesStatusUseCase: UpdateInvoicesStatusUseCase
	private _updateTransactionCreatedDateUseCase: UpdateTransactionCreatedDateUseCase
	private _updateInvoiceUseCase: UpdateInvoiceUseCase

	isFetching: boolean = false
	limit = 400
	isLoading: boolean = false
	error: Error = { message: "", type: undefined }
	editMode: boolean = false
	invoices: (Invoice | InvoiceAggregate)[] = []
	selectedInvoiceIds: number[] = []
	seletectInvoicesTransactionsIds: number[] = []
	selectedInvoice: Invoice | InvoiceAggregate | null = null
	selectedStatus: InvoiceStatus = InvoiceStatus.EMITTED
	tableConfig: TableConfig = {
		pageSize: 20,
		sort: { order: "descend", field: "id" }
	}
	filter: Partial<InvoicesFilter> = { aggregate: true }
	invoiceNumberStart: number | null = null

	constructor({
		ErrorHandler,
		GetInvoicesUseCase,
		ExportInvoicesUseCase,
		ExportInvoiceTransactionsUseCase,
		UpdateInvoicesStatusUseCase,
		UpdateTransactionCreatedDateUseCase,
		UpdateInvoiceUseCase
	}: Props) {
		super()
		makeObservable(this, {
			isFetching: observable,
			isLoading: observable,
			invoices: observable,
			selectedInvoiceIds: observable,
			selectedInvoice: observable,
			seletectInvoicesTransactionsIds: observable,
			selectedStatus: observable,
			invoiceNumberStart: observable,
			filter: observable,
			setInvoices: action,
			setLoading: action,
			setIsFetching: action,
			setSelectedInvoiceIds: action,
			setSelectedInvoice: action,
			setSeletectInvoicesTransactionsIds: action,
			cleanPagination: action,
			setSelectedStatus: action,
			setInvoiceNumberStart: action,
			setFilter: action
		})
		this._errorHandler = ErrorHandler
		this._getInvoicesUseCase = GetInvoicesUseCase
		this._exportInvoicesUseCase = ExportInvoicesUseCase
		this._updateInvoicesStatusUseCase = UpdateInvoicesStatusUseCase
		this._updateTransactionCreatedDateUseCase = UpdateTransactionCreatedDateUseCase
		this._exportInvoiceTransactionsUseCase = ExportInvoiceTransactionsUseCase
		this._updateInvoiceUseCase = UpdateInvoiceUseCase
		this.fetchInvoices(this.filter)
	}

	//Aggregates invoices by seller
	private aggregateInvoices(invoices: Invoice[]): (Invoice | InvoiceAggregate)[] {
		const sellerAggregate = invoices.reduce(
			(aggregate, invoice) => ({
				...aggregate,
				[`${invoice.sellerId}-${invoice.period}`]: (
					aggregate[`${invoice.sellerId}-${invoice.period}`] ?? []
				).concat([invoice])
			}),
			{} as { [key: string]: Invoice[] }
		)
		return Object.values(sellerAggregate).map(sellerInvoices =>
			sellerInvoices.length > 1
				? {
						invoices: sellerInvoices,
						invoiceNumber: sellerInvoices.every(i => i.invoiceNumber === sellerInvoices[0].invoiceNumber)
							? sellerInvoices[0].invoiceNumber
							: undefined,
						createdAt: sellerInvoices[0].createdAt,
						paymentDate: sellerInvoices.every(i => i.paymentDate === sellerInvoices[0].paymentDate)
							? sellerInvoices[0].paymentDate
							: null,
						emissionDate: sellerInvoices.every(i => i.emissionDate === sellerInvoices[0].emissionDate)
							? sellerInvoices[0].emissionDate
							: null,
						period: sellerInvoices[0].period,
						sellerId: sellerInvoices[0].sellerId,
						sellerOfficeId: sellerInvoices.every(i => i.sellerOfficeId === sellerInvoices[0].sellerOfficeId)
							? sellerInvoices[0].sellerOfficeId
							: undefined,
						status: sellerInvoices.every(i => i.status === sellerInvoices[0].status)
							? sellerInvoices[0].status
							: undefined,
						total: Number(sellerInvoices.reduce((sum, invoice) => sum + invoice.total, 0).toFixed(2)),
						totalFee: Number(sellerInvoices.reduce((sum, invoice) => sum + invoice.totalFee, 0).toFixed(2)),
						transactions: sellerInvoices.reduce(
							(transactions, invoice) => transactions.concat(invoice.transactions ?? []),
							[] as InvoiceTransaction[]
						),
						sellerPrice: Number(
							sellerInvoices.reduce((sum, invoice) => sum + invoice.sellerPrice, 0).toFixed(2)
						),
						sellerPayment: Number(
							sellerInvoices.reduce((sum, invoice) => sum + invoice.sellerPayment, 0).toFixed(2)
						),
						sellerPaymentDate: sellerInvoices.every(i => i.status === sellerInvoices[0].sellerPaymentDate)
							? sellerInvoices[0].sellerPaymentDate
							: null,
						sellerCollectionDate: sellerInvoices.every(
							i => i.status === sellerInvoices[0].sellerCollectionDate
						)
							? sellerInvoices[0].sellerCollectionDate
							: null,
						seller: sellerInvoices[0].seller
				  }
				: sellerInvoices[0]
		)
	}

	public updateInvoicesField(invoiceId: number[], field: keyof Invoice, value: any) {
		const updated = this.invoices.map(i => {
			if (i.id) {
				return invoiceId.includes(i.id) ? { ...i, [field]: value } : i
			} else {
				let modified = false
				const aggregatedInvoice: InvoiceAggregate = {
					...i,
					invoices: (i as InvoiceAggregate).invoices.map(aggregated => {
						modified = modified || invoiceId.includes(aggregated.id)
						return invoiceId.includes(aggregated.id) ? { ...aggregated, [field]: value } : aggregated
					})
				}
				return {
					...aggregatedInvoice,
					[field]:
						modified &&
						aggregatedInvoice.invoices.every(
							aggregated => aggregated[field] === aggregatedInvoice.invoices[0][field]
						)
							? value
							: aggregatedInvoice[field]
				}
			}
		})
		this.setInvoices(updated)
	}

	public async fetchInvoices(filter?: Partial<InvoicesFilter>) {
		try {
			this.setIsFetching(true)
			this.setFilter(filter ?? {})
			const invoices = await this._getInvoicesUseCase.exec({
				offset: 0,
				limit: this.limit,
				...this.filter
			})
			const selectedInvoice =
				invoices.find(
					i =>
						(this.selectedInvoice?.id && i.id === this.selectedInvoice?.id) ||
						(this.selectedInvoice as InvoiceAggregate)?.invoices?.some(selected => selected.id === i.id)
				) ?? null
			this.setSelectedInvoice(selectedInvoice)
			this.setSelectedInvoiceIds([])
			this.setInvoices(filter?.aggregate ? this.aggregateInvoices(invoices) : invoices)
			this.cleanPagination()
		} catch (error: any) {
			throw this._errorHandler.handleError(error)
		} finally {
			this.setIsFetching(false)
		}
	}

	public async exportInvoices(filter?: InvoicesFilter) {
		try {
			this.setLoading(true)
			this.setFilter(filter ?? {})
			const file = await this._exportInvoicesUseCase.exec({
				offset: 0,
				limit: this.limit,
				...this.filter
			})
			const fileService = new FileService()
			const dateString = new Date().toISOString().split("T")[0]
			fileService.dowloadFile(file, `Facturación_${dateString}.csv`)
		} catch (error: any) {
			throw this._errorHandler.handleError(error)
		} finally {
			this.setLoading(false)
		}
	}

	public async exportInvoiceTransactions(invoiceId: number[]) {
		try {
			this.setLoading(true)
			const file = await this._exportInvoiceTransactionsUseCase.exec(invoiceId)
			const fileService = new FileService()
			const dateString = new Date().toISOString().split("T")[0]
			fileService.dowloadFile(file, `Facturación_Transacciones_${dateString}.csv`)
		} catch (error: any) {
			throw this._errorHandler.handleError(error)
		} finally {
			this.setLoading(false)
		}
	}

	public async updateInvoicesStatus() {
		if ((this.selectedInvoiceIds?.length ?? 0) === 0) return
		try {
			this.setLoading(true)
			await this._updateInvoicesStatusUseCase.exec({
				invoiceIds: this.selectedInvoiceIds,
				status: this.selectedStatus,
				invoiceNumberStart: this.invoiceNumberStart
			})
			this.fetchInvoices(this.filter)
		} catch (error: any) {
			throw this._errorHandler.handleError(error)
		} finally {
			this.setLoading(false)
		}
	}

	public async updateInvoiceEmissionDate(invoiceId: number[], emissionDate: string) {
		try {
			this.updateInvoicesField(invoiceId, "emissionDate", emissionDate)
			this.setLoading(true)
			await this._updateInvoiceUseCase.exec({ invoiceId, emissionDate: emissionDate })
		} catch (error: any) {
			throw this._errorHandler.handleError(error)
		} finally {
			this.setLoading(false)
		}
	}

	public async updateInvoiceNumber(invoiceId: number[], invoiceNumber: string) {
		try {
			this.updateInvoicesField(invoiceId, "invoiceNumber", invoiceNumber)
			this.setLoading(true)
			await this._updateInvoiceUseCase.exec({ invoiceId, invoiceNumber })
		} catch (error: any) {
			throw this._errorHandler.handleError(error)
		} finally {
			this.setLoading(false)
		}
	}

	public async updateTransactionCreatedDate(invoiceId: number, createdDate: Date) {
		if ((this.seletectInvoicesTransactionsIds?.length ?? 0) === 0) return
		try {
			this.setLoading(true)
			await this._updateTransactionCreatedDateUseCase.exec({
				invoiceId,
				createdDate,
				transactionIds: this.seletectInvoicesTransactionsIds
			})
			this.setSeletectInvoicesTransactionsIds([])
			await this.fetchInvoices()
			if (this.selectedInvoice === null) {
				window.location.href = "/billing"
			}
		} catch (error: any) {
			throw this._errorHandler.handleError(error)
		} finally {
			this.setLoading(false)
		}
	}

	public cleanPagination() {
		this.pagination = 0
		this.setHasNextPage(true)
	}

	// ACTIONS
	setInvoices(invoices: (Invoice | InvoiceAggregate)[]) {
		this.invoices = invoices
	}

	setSelectedInvoiceIds(selectedInvoiceIds: number[]) {
		this.selectedInvoiceIds = selectedInvoiceIds
	}

	setSeletectInvoicesTransactionsIds(seletectInvoicesTransactionsIds: number[]) {
		this.seletectInvoicesTransactionsIds = seletectInvoicesTransactionsIds
	}

	setSelectedInvoice(invoice: Invoice | InvoiceAggregate | null) {
		this.selectedInvoice = invoice
	}

	setSelectedStatus(selectedStatus: InvoiceStatus) {
		this.selectedStatus = selectedStatus
	}

	setLoading(isLoading: boolean) {
		this.isLoading = isLoading
	}

	setIsFetching(isFetching: boolean) {
		this.isFetching = isFetching
	}

	setError(message: string, type: ErrorType) {
		this.error = { message, type }
	}

	setTableConfig(config: Partial<typeof this.tableConfig>) {
		this.tableConfig = { ...this.tableConfig, ...config }
	}

	setInvoiceNumberStart(invoiceNumberStart: number | null) {
		this.invoiceNumberStart = invoiceNumberStart
	}

	setFilter(filter: Partial<InvoicesFilter>) {
		this.filter = filter
	}
}
